import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { CheckCircle2, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TransportationInterestModal = ({ open, onOpenChange }) => {
  const [formData, setFormData] = useState({
    // Parent/Guardian Information
    guardianName: '',
    email: '',
    phone: '',
    
    // Student Information
    studentName: '',
    studentAge: '',
    studentGrade: '',
    
    // Transportation Details
    pickupLocation: '',
    dropoffLocation: '',
    scheduleNeeds: '',
    transportationDays: '',
    
    // Special Requirements
    specialRequirements: '',
    behavioralNeeds: '',
    medicalNeeds: '',
    
    // Additional Info
    hearAboutUs: '',
    additionalNotes: '',
    
    // Key Questions
    questionMissedSchool: '',
    questionOperationalStrain: '',
    questionCoordinatingTime: '',
    questionConsistency: ''
  });
  
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/transportation-interest`, formData);
      console.log('Transportation interest submitted:', response.data);
      setSubmitted(true);
      
      setTimeout(() => {
        setSubmitted(false);
        onOpenChange(false);
        setFormData({
          guardianName: '',
          email: '',
          phone: '',
          studentName: '',
          studentAge: '',
          studentGrade: '',
          pickupLocation: '',
          dropoffLocation: '',
          scheduleNeeds: '',
          transportationDays: '',
          specialRequirements: '',
          behavioralNeeds: '',
          medicalNeeds: '',
          hearAboutUs: '',
          additionalNotes: '',
          questionMissedSchool: '',
          questionOperationalStrain: '',
          questionCoordinatingTime: '',
          questionConsistency: ''
        });
      }, 3000);
    } catch (err) {
      console.error('Error submitting form:', err);
      setError('Failed to submit. Please try again or call us at (555) 365-RIDE.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectChange = (name, value) => {
    setFormData({
      ...formData,
      [name]: value
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        {!submitted ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-3xl font-bold text-[#0B0F14]">
                Request Transportation Services
              </DialogTitle>
              <DialogDescription className="text-lg text-[#5F6B7A]">
                Get specialized, safe transportation for your student. Fill out this form and we'll contact you within 24 hours.
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-6 mt-6">
              {/* Parent/Guardian Information */}
              <div className="border-l-4 border-[#0077BE] pl-4">
                <h3 className="text-xl font-bold text-[#0B0F14] mb-4">Parent/Guardian Information</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <Label htmlFor="guardianName" className="text-[#0B0F14] font-semibold">
                      Full Name *
                    </Label>
                    <Input
                      id="guardianName"
                      name="guardianName"
                      value={formData.guardianName}
                      onChange={handleChange}
                      required
                      className="mt-2"
                      placeholder="Your full name"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="email" className="text-[#0B0F14] font-semibold">
                      Email Address *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="mt-2"
                      placeholder="your@email.com"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="phone" className="text-[#0B0F14] font-semibold">
                      Phone Number *
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="mt-2"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>
              </div>

              {/* Student Information */}
              <div className="border-l-4 border-[#00B4D8] pl-4">
                <h3 className="text-xl font-bold text-[#0B0F14] mb-4">Student Information</h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="studentName" className="text-[#0B0F14] font-semibold">
                      Student Name *
                    </Label>
                    <Input
                      id="studentName"
                      name="studentName"
                      value={formData.studentName}
                      onChange={handleChange}
                      required
                      className="mt-2"
                      placeholder="Student's full name"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="studentAge" className="text-[#0B0F14] font-semibold">
                      Age *
                    </Label>
                    <Input
                      id="studentAge"
                      name="studentAge"
                      type="number"
                      value={formData.studentAge}
                      onChange={handleChange}
                      required
                      className="mt-2"
                      placeholder="Age"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="studentGrade" className="text-[#0B0F14] font-semibold">
                      Grade Level *
                    </Label>
                    <Select onValueChange={(value) => handleSelectChange('studentGrade', value)} required>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select grade" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pre-k">Pre-K</SelectItem>
                        <SelectItem value="kindergarten">Kindergarten</SelectItem>
                        <SelectItem value="1">1st Grade</SelectItem>
                        <SelectItem value="2">2nd Grade</SelectItem>
                        <SelectItem value="3">3rd Grade</SelectItem>
                        <SelectItem value="4">4th Grade</SelectItem>
                        <SelectItem value="5">5th Grade</SelectItem>
                        <SelectItem value="6">6th Grade</SelectItem>
                        <SelectItem value="7">7th Grade</SelectItem>
                        <SelectItem value="8">8th Grade</SelectItem>
                        <SelectItem value="9">9th Grade</SelectItem>
                        <SelectItem value="10">10th Grade</SelectItem>
                        <SelectItem value="11">11th Grade</SelectItem>
                        <SelectItem value="12">12th Grade</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Transportation Details */}
              <div className="border-l-4 border-[#2ECC71] pl-4">
                <h3 className="text-xl font-bold text-[#0B0F14] mb-4">Transportation Details</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="pickupLocation" className="text-[#0B0F14] font-semibold">
                      Pickup Location *
                    </Label>
                    <Input
                      id="pickupLocation"
                      name="pickupLocation"
                      value={formData.pickupLocation}
                      onChange={handleChange}
                      required
                      className="mt-2"
                      placeholder="Home address or pickup point"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="dropoffLocation" className="text-[#0B0F14] font-semibold">
                      Dropoff Location *
                    </Label>
                    <Input
                      id="dropoffLocation"
                      name="dropoffLocation"
                      value={formData.dropoffLocation}
                      onChange={handleChange}
                      required
                      className="mt-2"
                      placeholder="School or destination"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="scheduleNeeds" className="text-[#0B0F14] font-semibold">
                      Preferred Schedule *
                    </Label>
                    <Input
                      id="scheduleNeeds"
                      name="scheduleNeeds"
                      value={formData.scheduleNeeds}
                      onChange={handleChange}
                      required
                      className="mt-2"
                      placeholder="e.g., 7:30 AM pickup, 3:00 PM dropoff"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="transportationDays" className="text-[#0B0F14] font-semibold">
                      Days Needed *
                    </Label>
                    <Select onValueChange={(value) => handleSelectChange('transportationDays', value)} required>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select days" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekdays">Weekdays (Mon-Fri)</SelectItem>
                        <SelectItem value="monday-thursday">Monday-Thursday</SelectItem>
                        <SelectItem value="specific">Specific Days (will discuss)</SelectItem>
                        <SelectItem value="daily">Daily (Mon-Sun)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Special Requirements */}
              <div className="border-l-4 border-[#0077BE] pl-4">
                <h3 className="text-xl font-bold text-[#0B0F14] mb-4">Special Requirements</h3>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="specialRequirements" className="text-[#0B0F14] font-semibold">
                      Special Needs or Accommodations
                    </Label>
                    <Textarea
                      id="specialRequirements"
                      name="specialRequirements"
                      value={formData.specialRequirements}
                      onChange={handleChange}
                      className="mt-2"
                      rows={2}
                      placeholder="Wheelchair accessibility, car seat, sensory needs, etc."
                    />
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="behavioralNeeds" className="text-[#0B0F14] font-semibold">
                        Behavioral Support Needs
                      </Label>
                      <Textarea
                        id="behavioralNeeds"
                        name="behavioralNeeds"
                        value={formData.behavioralNeeds}
                        onChange={handleChange}
                        className="mt-2"
                        rows={2}
                        placeholder="Autism, ADHD, anxiety, etc."
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="medicalNeeds" className="text-[#0B0F14] font-semibold">
                        Medical Considerations
                      </Label>
                      <Textarea
                        id="medicalNeeds"
                        name="medicalNeeds"
                        value={formData.medicalNeeds}
                        onChange={handleChange}
                        className="mt-2"
                        rows={2}
                        placeholder="Allergies, medications, emergency contacts, etc."
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label htmlFor="hearAboutUs" className="text-[#0B0F14] font-semibold">
                      How did you hear about us?
                    </Label>
                    <Select onValueChange={(value) => handleSelectChange('hearAboutUs', value)}>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select option" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="google">Google Search</SelectItem>
                        <SelectItem value="referral">Referral from Friend/Family</SelectItem>
                        <SelectItem value="school">School/District Referral</SelectItem>
                        <SelectItem value="social-media">Social Media</SelectItem>
                        <SelectItem value="website">Website</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="additionalNotes" className="text-[#0B0F14] font-semibold">
                    Additional Notes
                  </Label>
                  <Textarea
                    id="additionalNotes"
                    name="additionalNotes"
                    value={formData.additionalNotes}
                    onChange={handleChange}
                    className="mt-2"
                    rows={3}
                    placeholder="Any other information you'd like us to know..."
                  />
                </div>
              </div>

              {/* Key Questions Section */}
              <div className="border-l-4 border-[#FFB380] pl-4">
                <h3 className="text-xl font-bold text-[#0B0F14] mb-4">Help Us Understand Your Needs</h3>
                <p className="text-[#5F6B7A] mb-4">Please answer these questions to help us better serve your transportation needs:</p>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="questionMissedSchool" className="text-[#0B0F14] font-semibold">
                      Are there students who miss school because transportation can't flex to meet their needs?
                    </Label>
                    <Select onValueChange={(value) => handleSelectChange('questionMissedSchool', value)}>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select answer" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                        <SelectItem value="sometimes">Sometimes</SelectItem>
                        <SelectItem value="not-sure">Not Sure</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="questionOperationalStrain" className="text-[#0B0F14] font-semibold">
                      Do short-term changes create ongoing operational strain?
                    </Label>
                    <Select onValueChange={(value) => handleSelectChange('questionOperationalStrain', value)}>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select answer" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                        <SelectItem value="sometimes">Sometimes</SelectItem>
                        <SelectItem value="not-sure">Not Sure</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="questionCoordinatingTime" className="text-[#0B0F14] font-semibold">
                      Are teams spending time coordinating exceptions instead of supporting students?
                    </Label>
                    <Select onValueChange={(value) => handleSelectChange('questionCoordinatingTime', value)}>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select answer" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                        <SelectItem value="sometimes">Sometimes</SelectItem>
                        <SelectItem value="not-sure">Not Sure</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="questionConsistency" className="text-[#0B0F14] font-semibold">
                      Is consistency harder to maintain for highly mobile or vulnerable populations?
                    </Label>
                    <Select onValueChange={(value) => handleSelectChange('questionConsistency', value)}>
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select answer" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="yes">Yes</SelectItem>
                        <SelectItem value="no">No</SelectItem>
                        <SelectItem value="sometimes">Sometimes</SelectItem>
                        <SelectItem value="not-sure">Not Sure</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {error && (
                <div className="p-4 rounded-xl bg-red-50 border border-red-200">
                  <p className="text-red-600 font-semibold">{error}</p>
                </div>
              )}

              <div className="flex gap-4">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 bg-gradient-to-r from-[#0077BE] to-[#00B4D8] text-white hover:shadow-xl py-6 text-lg font-semibold rounded-xl"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Request'
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  disabled={isSubmitting}
                  className="px-8 py-6 rounded-xl"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </>
        ) : (
          <div className="py-12 text-center">
            <div className="w-20 h-20 rounded-full bg-[#2ECC71]/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-12 h-12 text-[#2ECC71]" />
            </div>
            <h3 className="text-2xl font-bold text-[#0B0F14] mb-3">
              Request Received!
            </h3>
            <p className="text-lg text-[#5F6B7A] mb-2">
              Thank you for your interest in our transportation services.
            </p>
            <p className="text-[#5F6B7A]">
              Our team will review your request and contact you within 24 hours to discuss your student's transportation needs.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TransportationInterestModal;
